import { DatePipe } from '@angular/common';
import {
  Component,
  ElementRef,
  Inject,
  Input, OnChanges,
  OnDestroy,
  OnInit,
  Optional,
  Self, SimpleChanges,
  ViewChild
} from '@angular/core';
import {
  FormBuilder,
  NgControl
} from '@angular/forms';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { NgbModal, NgbModalOptions } from '@ng-bootstrap/ng-bootstrap';
import { Observable, Subscription } from 'rxjs';
import {
  ActiveDeviceInMessage,
  DokumentInMessage,
  InitInMessage,
  InitOutMessage,
  ScankomponenteLibMockData,
  ScankomponenteLibService,
  StompConnectionState
} from 'scankomponente-lib';
import { APP_CONFIG } from '../../../app.config';
import { AppService } from '../../../common/app.service';
import { AssistantPopoverService } from '../../../common/assistant-popover.service';
import { DocumentService, DocumentSettings } from '../../../common/document.service';
import { LanguageService } from '../../../common/language.service';
import { ScankomponenteService, ScankomponenteSettings } from '../../../common/scan-component.service';
import { ValueListsService } from '../../../common/value-lists.service';
import { AppConfig } from '../../../models/config/app-config';
import { ValueListItem } from '../../../models/rest/valuelist/valuelist';
import { VisumDokument } from '../../../models/visum.dokument';
import { VisumErfassungSubFormComponent } from '../visum-erfassung-sub-form/visum-erfassung-sub-form.component';
import { ScannedDokument } from './scanned.document';
import { DokumentValidationService } from '../../../common/document-validator.service';


@Component({
  selector: 'app-dokumente',
  templateUrl: './dokumente.component.html',
  styleUrls: ['./dokumente.component.scss']
})
export class DokumenteComponent extends VisumErfassungSubFormComponent implements OnInit, OnChanges, OnDestroy {

  static readonly DATE_TIME_FORMAT = 'dd.MM.yyyy HH:mm:ss';

  private subscriptions: Subscription = new Subscription();
  scankomponenteSettings: ScankomponenteSettings;
  documentSettings: DocumentSettings;

  dokumentartListItems: ValueListItem[] = [];
  pdfDokumentList: ScannedDokument[] = [];

  // Hier wird jeweils eine temporäre URL für das gerade anzuzeigende PDF gespeichert
  viewPdfUrl: any;

  activeDevice$ = new Observable<ActiveDeviceInMessage>();
  apply$ = new Observable<DokumentInMessage>();
  cancel$ = new Observable<void>();
  connectionState$ = new Observable<string>();
  error$ = new Observable<any>();
  init$ = new Observable<InitInMessage>();

  safeResourceUrl: SafeResourceUrl;
  isDialogDisplayed: boolean;
  isConnected: boolean;
  isButtonEnabled = true;

  isDokumentPflicht$: Observable<boolean>;
  needsBADocument$: Observable<boolean>;

  @ViewChild('viewPDFDocument', { static: true })
  viewPDFDocument: ElementRef;

  @ViewChild('errorDialog', { static: true })
  errorDialog: ElementRef;

  @ViewChild('iframe', { static: true })
  iframe: ElementRef;

  @Input() hasErwerbstaetigkeit = false;

  @Input()
  set bereitsVorhandeneDokumente(dokumentListe: VisumDokument[]) {
    if (!dokumentListe || dokumentListe.length === 0) return;

    dokumentListe.forEach(dokument => {
      const scanned = this.convertToScannedDokument(dokument);
      try { scanned.barelevant = this.documentService.isBADocument(scanned); } catch {}
      this.pdfDokumentList.push(scanned);
    });

    this.form.patchValue({ pdfDocuments: [...this.pdfDokumentList] });
    this.touchAndRevalidateDocs();
  }

  private convertToScannedDokument(dokument: VisumDokument): ScannedDokument {
    return {
      data: dokument.dokument,
      sizeInBytes: dokument.groesseInBytes,
      doctype: dokument.art.valueListItemKey,
      creationDateAndTime: dokument.digitialisierungsdatum,
      numPages: 0,
      thumbnail: null,
      empfangenUeberAuslandsportal: true
    };
  }

  get hasAlreadyCapturedMaxNumOfPages(): boolean {
    return this.scankomponenteSettings.maxCapturedPages <= this.numberOfScannedPages;
  }

  get numberOfRemainingPagesToScan(): number {
    const numberOfRemainingPages = this.scankomponenteSettings.maxCapturedPages - this.numberOfScannedPages;
    return (0 < numberOfRemainingPages) ? numberOfRemainingPages : 0;
  }

  get numberOfScannedPages(): number {
    let result = 0;
    this.pdfDokumentList.forEach(value => {
      result += value.numPages;
    });
    return result;
  }

  get numberOfRemainingABHDocumentsToScan(): number {
    const numberOfRemainingABHDocuments = this.documentSettings.abhMaxNumberOfFiles - this.numberOfScannedABHDocuments;
    return (0 < numberOfRemainingABHDocuments) ? numberOfRemainingABHDocuments : 0;
  }

  get numberOfScannedABHDocuments(): number {
    let result = 0;
    this.pdfDokumentList.forEach(value => {
      if (this.documentService.isBADocument(value) === false) {
        result++;
      }
    });
    return result;
  }

  get numberOfRemainingBADocumentsToScan(): number {
    const numberOfRemainingBADocuments = this.documentSettings.baMaxNumberOfFiles - this.numberOfScannedBADocuments;
    return (0 < numberOfRemainingBADocuments) ? numberOfRemainingBADocuments : 0;
  }

  get numberOfScannedBADocuments(): number {
    let result = 0;
    this.pdfDokumentList.forEach(value => {
      if (this.documentService.isBADocument(value)) {
        result++;
      }
    });
    return result;
  }

  get isButtonEnabledMaxNumberDocuments(): boolean {
    let doctype = this.dokumentartListItems.find(value => value.value == this.form.value.dokumentart);
    if (doctype == null) {
      return true;
    }
    if (this.documentService.isValueListBADocument(doctype)) {
      return 0 < this.numberOfRemainingBADocumentsToScan;
    } else {
      return 0 < this.numberOfRemainingABHDocumentsToScan;
    }
  }

  get isButtonEnabledInGUI(): boolean {
    return this.isButtonEnabled
      && (0 < this.numberOfRemainingPagesToScan)
      && this.isButtonEnabledMaxNumberDocuments
      && this.form.value.dokumentart
      && (this.form.value.dokumentart !== '');
  }

  get isDocumentListVisible(): boolean {
    return this.pdfDokumentList && (0 < this.pdfDokumentList.length);
  }

  constructor(
    readonly fb: FormBuilder,
    private sanitizer: DomSanitizer,
    private valueListsService: ValueListsService,
    private modalService: NgbModal,
    private languageService: LanguageService,
    private scankomponenteRestService: ScankomponenteService,
    private scankomponenteLibService: ScankomponenteLibService,
    protected documentService: DocumentService,
    private datePipe: DatePipe,
    private readonly dokVal: DokumentValidationService,
    assistantPopoverService: AssistantPopoverService,
    @Optional() @Self() controlDir: NgControl,
    @Inject(APP_CONFIG) public appConfig: AppConfig,
    private readonly appService: AppService
  ) {
      super(controlDir, assistantPopoverService);

      this.scankomponenteLibService.deactivate();
      this.initScankomponenteLibObservables(scankomponenteLibService);
      this.form = this.wrapFormGroup(this.fb.group({
      bereitsVorhandeneDokumente: [null],
      scannertyp: [null],
      dokumentart: [null],
      pdfDocuments: [[], [ this.dokVal.technicalDocsValidator() ]],
      reisegrundErwerbstaetigkeit: [this.hasErwerbstaetigkeit]
    }));

    this.form.setValidators(this.dokVal.businessGroupValidator());
    this.form.updateValueAndValidity({ emitEvent: false });

    this.subscriptions.add(this.dokVal.wireUp(this.form));

    this.isDokumentPflicht$ = this.dokVal.isDokumentPflicht$(this.form);
    this.needsBADocument$  = this.dokVal.needsBADocument$(this.form);
  }

  writeValue(value: any): void {
    value = this.fillInFields(value);
    this.pdfDokumentList = value.pdfDocuments || [];
    this.form.setValue(value, { emitEvent: false });
    this.form.controls.dokumentart.markAsTouched();
  }

  ngOnInit() {
    super.ngOnInit();

    this.initializeComponentState();

    this.subscriptions.add(
      this.valueListsService.getValueListItems('dokument_art').subscribe((listItems: ValueListItem[]) => {
        this.dokumentartListItems = listItems;
      })
    );

    this.initScankomponenteRestSubscriptions();
    this.initScankomponenteLibSubscriptions();

    this.scankomponenteRestService.checkAndStartScankomponente();
    this.form.controls.dokumentart.markAsTouched();

    this.subscriptions.add(
      this.valueListsService.getValueListItems('dokument_art').subscribe((listItems: ValueListItem[]) => {
        this.dokumentartListItems = listItems;
        this.form.updateValueAndValidity({ emitEvent: true });
      })
    );
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['hasErwerbstaetigkeit']) {
      this.form.get('reisegrundErwerbstaetigkeit')
        ?.setValue(this.hasErwerbstaetigkeit, { emitEvent: true });
      this.form.updateValueAndValidity({ onlySelf: false, emitEvent: true });
    }
  }

  private initializeComponentState() {
    this.pdfDokumentList = [];
    this.form.patchValue({
      scannertyp: null,
      dokumentart: null,
      pdfDocuments: []
    });
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.scankomponenteLibService.deactivate();
  }

  private initScankomponenteRestSubscriptions() {
    this.subscriptions.add(
      this.scankomponenteRestService.errorReceived.subscribe(error => {
        console.error("InitScankomponenteRest", error);
        this.showErrorDialog();
      })
    );

    this.subscriptions.add(
      this.scankomponenteRestService.getSettings().subscribe((scankomponenteSettings: ScankomponenteSettings) => {
        this.scankomponenteSettings = scankomponenteSettings;
      })
    );

    this.subscriptions.add(
      this.documentService.getSettings().subscribe((documentSettings: DocumentSettings) => {
        this.documentSettings = documentSettings;
      })
    );
  }

  private initScankomponenteLibObservables(service: ScankomponenteLibService): void {
    this.activeDevice$ = service.activeDevice$;
    this.apply$ = service.apply$;
    this.cancel$ = service.cancel$;
    this.connectionState$ = service.connectionState$;
    this.error$ = service.error$;
    this.init$ = service.init$;
  }

  private initScankomponenteLibSubscriptions(): void {
    this.subscriptions.add(
      this.activeDevice$.subscribe((message: ActiveDeviceInMessage) => {
        this.form.value.scannertyp = message.name;
      })
    );

    this.subscriptions.add(
      this.apply$.subscribe((message: DokumentInMessage) => {
        this.closeScankomponenteDialog();
        this.onDocumentsScanResponseReceived(message);
      })
    );

    this.subscriptions.add(
      this.cancel$.subscribe(() => {
        this.closeScankomponenteDialog();
      })
    );

    this.subscriptions.add(
      this.connectionState$.subscribe((state: string) => {
        this.isConnected = (StompConnectionState.OPEN === state);

        if (this.isConnected) {
          this.onConnect();
        }
      })
    );

    this.subscriptions.add(
      this.error$.subscribe((error: any) => {
        this.onError(error);
      })
    );

    this.subscriptions.add(
      this.init$.subscribe((message: InitInMessage) => {
        this.safeResourceUrl = message.url;
        this.form.value.scannertyp = message.deviceName;
        this.openScankomponenteDialog(this.safeResourceUrl);
      })
    );
  }

  onConnect(): void {
    const isShowGui = this.scankomponenteRestService.isShowScankomponenteGui;
    if (isShowGui) {
      this.sendInit();
    } else {
      this.sendMockResponse();
    }
  }

  onError(error: any): void {
    console.error("Error Response: ", error)
    this.scankomponenteLibService.deactivate();
    this.showErrorDialog();
  }

  private showErrorDialog(): void {
    const options = {
      centered: true,
      closeOnOutsideClick: true,
      size: 'lg'
    } as NgbModalOptions;
    this.modalService.open(this.errorDialog, options);
  }

  onScanClicked(): void {
    const isShowGui = this.scankomponenteRestService.isShowScankomponenteGui;
    if (isShowGui) {
      this.scankomponenteLibService.deactivate();
      this.scankomponenteLibService.activate();
      this.temporarilyDisableScanButton();
    } else {
      // Im Mock-Modus ohne GUI wird keine Websocket-Verbindung aufgebaut, daher hier ein direkter Aufruf.
      this.onConnect();
    }
  }

  private temporarilyDisableScanButton(): void {
    this.isButtonEnabled = false;
    setTimeout(() => {
      this.isButtonEnabled = true;
    }, 10000);
  }

  private sendMockResponse(): void {
    const mockMessage: DokumentInMessage = ScankomponenteLibMockData.threePages as DokumentInMessage;
    mockMessage.documentType = this.form.value.dokumentart;
    this.onDocumentsScanResponseReceived(mockMessage);
  }

  private sendInit(): void {
    const initMessage = {
      language: this.languageService.getLanguage(),
      pageLimit: this.numberOfRemainingPagesToScan,
      documentType: this.form.value.dokumentart,
      autoScan: this.scankomponenteSettings.autoScan,
      devices: this.scankomponenteSettings.whitelist,
      grayScale: this.scankomponenteSettings.grayScale,
      jpegQuality: this.scankomponenteSettings.compressionLevel,
      manualSelect: this.scankomponenteSettings.manualSelect,
      openInNewTab: false,
      resolution: this.scankomponenteSettings.resolutionDpi
    } as InitOutMessage;
    this.scankomponenteLibService.sendInit(initMessage);
  }

  closeScankomponenteDialog(): void {
    this.isDialogDisplayed = false;
    this.iframe.nativeElement.src = '';
    this.scankomponenteLibService.deactivate();
  }

  openScankomponenteDialog(url: SafeResourceUrl): void {
    this.isDialogDisplayed = true;
    this.iframe.nativeElement.hidden = true;
    this.iframe.nativeElement.src = url;
  }

  onFrameLoad() {
    // Workaround für Bug in Chrome Version 87:
    // https://support.google.com/chrome/thread/85275954?hl=en
    setTimeout(() => {
      this.iframe.nativeElement.hidden = false;
    }, 500);
  }

  showPdfFromList(elemNum: number) {
    const binary = atob(this.pdfDokumentList[elemNum].data);
    const array = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      array[i] = binary.charCodeAt(i);
    }
    const blob = new Blob([array], { type: 'application/pdf' });

    this.viewPdfUrl = this.sanitizer.bypassSecurityTrustResourceUrl(URL.createObjectURL(blob));
    this.modalService.open(this.viewPDFDocument, { size: 'lg' }).result.then(
      () => {
        URL.revokeObjectURL(this.viewPdfUrl);
      }
    );
  }

  getDokumentArtKey(value: string): string {
    const item = this.dokumentartListItems.find((listOption: ValueListItem) => {
      return listOption.value === value;
    });
    return item ? item.key : value;
  }

  getPDFSizeFromBase64String(base46String: string): number {
    return base46String.length * 0.75; // Das ergibt ungefähr die Binärgröße
  }

  getDokumentArtDisplayValue(key: string): string {
    const item = this.dokumentartListItems.find((listOption: ValueListItem) => {
      return listOption.key === key;
    });
    return item ? item.value : key;
  }

  isDocumentSizeInvalid(dokument: ScannedDokument): boolean {
    return this.documentService.isDocumentSizeInvalid(dokument);
  }

  private touchAndRevalidateDocs(): void {
    this.form.get('pdfDocuments')?.markAsTouched();
    this.form.updateValueAndValidity({ emitEvent: false });
  }

  // By adding a documnt
  private onDocumentsScanResponseReceived(response: DokumentInMessage) {
    if (response.pdf) {
      const doc: ScannedDokument = {
        doctype: this.getDokumentArtKey(response.documentType),
        thumbnail: response.thumbnail,
        data: response.pdf,
        sizeInBytes: this.getPDFSizeFromBase64String(response.pdf),
        numPages: response.numberOfPages,
        creationDateAndTime: this.datePipe.transform(new Date(), DokumenteComponent.DATE_TIME_FORMAT),
        empfangenUeberAuslandsportal: false
      };
      const item = this.dokumentartListItems.find(v => v.key === doc.doctype);
      if (item) doc.barelevant = item.fields?.barelevant;

      this.pdfDokumentList.push(doc);
      this.form.patchValue({ pdfDocuments: [...this.pdfDokumentList] });
      this.touchAndRevalidateDocs();
    }
  }

  removePdfFromList(elemNum: number) {
    this.pdfDokumentList.splice(elemNum, 1);
    this.form.patchValue({ pdfDocuments: [...this.pdfDokumentList] });
    this.touchAndRevalidateDocs();  // ← macht das Feld wieder invalide, wenn leer
  }
}
